import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Upload, Download, Files, CheckCircle, AlertCircle } from "lucide-react";

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "-";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
};

export const UploadSection = ({
  fileInputRef, handleFileUpload, uploading,
  customLedgerName, setCustomLedgerName,
  downloadTemplate, uploadResult, validationResult, validating
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <Upload className="w-5 h-5" />
        Upload TDS Transactions
        <span className="text-sm font-normal text-slate-500 ml-2">(Multiple files supported)</span>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <Label htmlFor="ledger-name" className="text-sm font-medium text-slate-700">
          TDS Ledger Name (Optional)
        </Label>
        <Input
          id="ledger-name"
          value={customLedgerName}
          onChange={(e) => setCustomLedgerName(e.target.value)}
          placeholder="e.g., TDS ON CONTRACT 194C, TDS ON PROFESSIONAL FEES 194J"
          className="mt-2"
          data-testid="ledger-name-input"
        />
        <p className="text-xs text-slate-500 mt-1">
          Specify the ledger name if not auto-detected from the file. This will be applied to all transactions in this upload.
        </p>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
          <Download className="w-4 h-4 mr-2" />
          Download CSV Template
        </Button>

        <div className="flex-1">
          <input
            type="file"
            accept=".xml,.csv,.txt,.xlsx,.xls"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            id="tds-file-upload"
            data-testid="tds-file-input"
            multiple
          />
          <label htmlFor="tds-file-upload">
            <Button
              as="span"
              disabled={uploading}
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-files-btn"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Files className="w-4 h-4 mr-2" />
                  Upload Files (XML/CSV/TXT)
                </>
              )}
            </Button>
          </label>
        </div>
      </div>

      {validating && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm text-blue-700">Validating import data...</span>
        </div>
      )}

      {validationResult && !uploadResult && (
        <div className={`p-4 rounded-lg border ${validationResult.error_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`} data-testid="validation-summary">
          <div className="flex items-start gap-3">
            {validationResult.error_count > 0 ? (
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-medium text-slate-900">
                Pre-Import Validation: {validationResult.total_rows} rows
              </p>
              <div className="grid grid-cols-4 gap-3 mt-2 text-sm">
                <div className="text-center p-2 bg-white rounded border">
                  <div className="font-bold text-slate-700">{formatCurrency(validationResult.summary?.total_amount)}</div>
                  <div className="text-xs text-slate-500">Total Amount</div>
                </div>
                <div className="text-center p-2 bg-white rounded border">
                  <div className="font-bold text-slate-700">{formatCurrency(validationResult.summary?.total_tds)}</div>
                  <div className="text-xs text-slate-500">Total TDS</div>
                </div>
                <div className="text-center p-2 bg-white rounded border">
                  <div className="font-bold text-amber-600">{validationResult.summary?.missing_pan || 0}</div>
                  <div className="text-xs text-slate-500">Missing PAN</div>
                </div>
                <div className="text-center p-2 bg-white rounded border">
                  <div className="font-bold text-amber-600">{validationResult.summary?.rate_mismatches || 0}</div>
                  <div className="text-xs text-slate-500">Rate Mismatches</div>
                </div>
              </div>
              {validationResult.error_count > 0 && (
                <div className="mt-2 text-sm text-amber-700">
                  {validationResult.errors.slice(0, 3).map((err) => (
                    <p key={`${err.row}-${err.field}`}>Row {err.row}: {err.message}</p>
                  ))}
                  {validationResult.error_count > 3 && <p>...and {validationResult.error_count - 3} more errors</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {uploadResult && (
        <div className={`p-4 rounded-lg border ${uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="upload-result">
          {uploadResult.success ? (
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Upload Successful!</p>
                <div className="text-sm text-green-700 mt-1 space-y-1">
                  <p>
                    {uploadResult.inserted} TDS transactions imported
                    {uploadResult.filesProcessed > 1 && ` from ${uploadResult.filesProcessed} files`}
                  </p>
                  {uploadResult.errors > 0 && <p className="text-amber-700">{uploadResult.errors} records had errors</p>}
                  {uploadResult.mismatches > 0 && (
                    <p className="text-amber-600 font-medium">{uploadResult.mismatches} transactions have rate mismatches - review in TDS Transactions</p>
                  )}
                  {uploadResult.skippedFiles?.length > 0 && (
                    <div className="text-amber-600">
                      <p className="font-medium">Skipped duplicate files:</p>
                      <ul className="list-disc list-inside ml-2">
                        {uploadResult.skippedFiles.map((sf) => (
                          <li key={sf.filename}>{sf.filename} ({sf.existingCount} existing records)</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="font-medium">
                    Total Amount: {formatCurrency(uploadResult.total_amount)} |
                    Total TDS: {formatCurrency(uploadResult.total_tds)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">Upload Failed</p>
                <p className="text-sm text-red-700">{uploadResult.error}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);
